/**
 * System › Settings — unified, searchable settings hub (2026-06-28).
 *
 * The single place to FIND any configuration surface in the product. It
 * AGGREGATES (links to) every settings/config page across modules — it does
 * NOT relocate domain config here (铁律: domain config stays in its domain;
 * this hub only points at it). Pages that live in another module are marked
 * "Opens in <area> ↗" so navigating away is never a surprise.
 *
 * Two render modes:
 *   • idle (no query) → grouped by area (Settings · Organization · Attendance ·
 *     Processes · Integrations · System), each a card grid.
 *   • searching → a single flat grid of every entry whose translated title /
 *     description / area matches the query (the "search" half of the ask).
 *
 * License gating: the Attendance group is hidden for a non-QuTime org, the
 * Processes group for a non-PureOA org (少即是多 — don't show config a product
 * doesn't have). Titles reuse the existing nav i18n keys (already ×9) so the
 * aggregation adds almost no new strings.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grid, Paper, Stack, TextField, InputAdornment } from '@mui/material';
import {
  SearchOutlined,
  NorthEastOutlined,
  ChevronRightOutlined,
  // Settings (configured here)
  GroupAddOutlined,
  LockOutlined,
  VpnKeyOutlined,
  NotificationsOutlined,
  CloudDownloadOutlined,
  // Organization
  BusinessOutlined,
  LocationOnOutlined,
  HubOutlined,
  WorkOutlineOutlined,
  BadgeOutlined,
  Diversity3Outlined,
  // Attendance
  PolicyOutlined,
  AltRouteOutlined,
  TimelapseOutlined,
  BeachAccessOutlined,
  EventNoteOutlined,
  GpsFixedOutlined,
  // Processes
  AdminPanelSettingsOutlined,
  DescriptionOutlined,
  // Integrations
  LinkOutlined,
  ApiOutlined,
  WebhookOutlined,
  // System
  SecurityOutlined,
  PhoneIphoneOutlined,
  PaymentOutlined,
  FactCheckOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { meApi } from '../../../api/me';

type AreaKey = 'settingsMain' | 'organization' | 'attendance' | 'processes' | 'integrations' | 'system';

interface SettingEntry {
  key: string;
  /** i18n key for the card title. */
  titleKey: string;
  /** i18n key for the description (only the "configured here" cards have one). */
  descKey?: string;
  icon: React.ReactNode;
  path: string;
  area: AreaKey;
  /** Lives in another module → show the "Opens in <area> ↗" affordance. */
  crossModule: boolean;
  /** Only show when the org licenses this product. */
  license?: 'qutime' | 'pureoa';
}

// Order is the idle-mode group order. settingsMain first (the things actually
// configured under System › Settings), then the cross-module areas, System last.
const AREA_ORDER: AreaKey[] = ['settingsMain', 'organization', 'attendance', 'processes', 'integrations', 'system'];

// Header label per area. settingsMain uses the existing "Settings" group label;
// the cross-module areas reuse system:settings.areas.* (organization/attendance
// existed; system/processes/integrations added this session).
const AREA_HEADER: Record<AreaKey, string> = {
  settingsMain: 'system:settings.groupMain',
  organization: 'system:settings.areas.organization',
  attendance: 'system:settings.areas.attendance',
  processes: 'system:settings.areas.processes',
  integrations: 'system:settings.areas.integrations',
  system: 'system:settings.areas.system',
};

const ENTRIES: SettingEntry[] = [
  // ── Configured here (System › Settings) — rich cards with descriptions.
  { key: 'workerDefaults', titleKey: 'system:settings.cards.workerDefaults.title', descKey: 'system:settings.cards.workerDefaults.description', icon: <GroupAddOutlined />, path: '/system/settings/workers', area: 'settingsMain', crossModule: false },
  { key: 'security', titleKey: 'system:settings.cards.security.title', descKey: 'system:settings.cards.security.description', icon: <LockOutlined />, path: '/system/settings/security', area: 'settingsMain', crossModule: false },
  { key: 'notifications', titleKey: 'system:settings.cards.notifications.title', descKey: 'system:settings.cards.notifications.description', icon: <NotificationsOutlined />, path: '/system/settings/notifications', area: 'settingsMain', crossModule: false },
  { key: 'dataExport', titleKey: 'system:settings.cards.dataExport.title', descKey: 'system:settings.cards.dataExport.description', icon: <CloudDownloadOutlined />, path: '/system/settings/data-export', area: 'settingsMain', crossModule: false },
  { key: 'sso', titleKey: 'system:settings.cards.sso.title', descKey: 'system:settings.cards.sso.description', icon: <VpnKeyOutlined />, path: '/system/settings/sso', area: 'settingsMain', crossModule: false },

  // ── Organization
  { key: 'orgProfile', titleKey: 'common:nav.orgProfile', icon: <BusinessOutlined />, path: '/organization/profile', area: 'organization', crossModule: true },
  { key: 'branchesLocations', titleKey: 'common:nav.branchesLocations', icon: <LocationOnOutlined />, path: '/organization/branches-locations', area: 'organization', crossModule: true },
  { key: 'departments', titleKey: 'common:nav.departments', icon: <HubOutlined />, path: '/organization/departments', area: 'organization', crossModule: true },
  { key: 'positions', titleKey: 'common:nav.positions', icon: <WorkOutlineOutlined />, path: '/organization/positions', area: 'organization', crossModule: true },
  { key: 'employmentTypes', titleKey: 'common:nav.employmentTypes', icon: <BadgeOutlined />, path: '/organization/employment-types', area: 'organization', crossModule: true },
  { key: 'workerGroups', titleKey: 'common:nav.workerGroups', icon: <Diversity3Outlined />, path: '/organization/worker-groups', area: 'organization', crossModule: true },

  // ── Attendance (QuTime)
  { key: 'globalRules', titleKey: 'common:nav.globalRules', icon: <PolicyOutlined />, path: '/attendance/rules/global', area: 'attendance', crossModule: true, license: 'qutime' },
  { key: 'approvalRouting', titleKey: 'common:nav.approvalRouting', icon: <AltRouteOutlined />, path: '/attendance/approvals/routing', area: 'attendance', crossModule: true, license: 'qutime' },
  { key: 'timetables', titleKey: 'common:nav.timetables', icon: <TimelapseOutlined />, path: '/attendance/shifts/timetables', area: 'attendance', crossModule: true, license: 'qutime' },
  { key: 'leaveTypes', titleKey: 'common:nav.leaveTypes', icon: <BeachAccessOutlined />, path: '/attendance/leave/types', area: 'attendance', crossModule: true, license: 'qutime' },
  { key: 'leavePolicies', titleKey: 'common:nav.leavePolicies', icon: <BeachAccessOutlined />, path: '/attendance/leave/policies', area: 'attendance', crossModule: true, license: 'qutime' },
  { key: 'publicHolidays', titleKey: 'common:nav.publicHolidays', icon: <EventNoteOutlined />, path: '/attendance/leave/public-holidays', area: 'attendance', crossModule: true, license: 'qutime' },
  { key: 'geofences', titleKey: 'common:nav.geofences', icon: <GpsFixedOutlined />, path: '/attendance/geofences/department', area: 'attendance', crossModule: true, license: 'qutime' },

  // ── Processes (PureOA)
  { key: 'processRoles', titleKey: 'common:nav.processRoles', icon: <AdminPanelSettingsOutlined />, path: '/processes/roles', area: 'processes', crossModule: true, license: 'pureoa' },
  { key: 'formComponents', titleKey: 'common:nav.formComponents', icon: <DescriptionOutlined />, path: '/processes/form-components', area: 'processes', crossModule: true, license: 'pureoa' },

  // ── Integrations
  { key: 'connectors', titleKey: 'common:nav.connectors', icon: <LinkOutlined />, path: '/integrations/connectors', area: 'integrations', crossModule: true },
  { key: 'apiKeys', titleKey: 'common:nav.apiKeys', icon: <ApiOutlined />, path: '/integrations/api', area: 'integrations', crossModule: true },
  { key: 'webhooks', titleKey: 'common:nav.webhooks', icon: <WebhookOutlined />, path: '/integrations/webhooks', area: 'integrations', crossModule: true },

  // ── System (other than the configured-here group)
  { key: 'rolesPermissions', titleKey: 'common:nav.roles', icon: <SecurityOutlined />, path: '/system/access-control/roles', area: 'system', crossModule: true },
  { key: 'mobileDevices', titleKey: 'common:nav.mobileDevices', icon: <PhoneIphoneOutlined />, path: '/system/devices', area: 'system', crossModule: true },
  { key: 'billing', titleKey: 'common:nav.billing', icon: <PaymentOutlined />, path: '/system/billing', area: 'system', crossModule: true },
  { key: 'auditLogs', titleKey: 'common:nav.auditLogs', icon: <FactCheckOutlined />, path: '/system/audit-logs/access', area: 'system', crossModule: true },
];

const SettingsLanding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  // Default both true so nothing flickers out after the license fetch on the
  // common bundle/single-product orgs; corrected once the dashboard responds.
  const [hasQutime, setHasQutime] = useState(true);
  const [hasPureoa, setHasPureoa] = useState(true);

  useEffect(() => {
    let cancelled = false;
    meApi.getDashboard()
      .then((d) => {
        const lp = (d as { licensed_products?: string[] }).licensed_products ?? [];
        if (cancelled) return;
        setHasQutime(lp.includes('qutime'));
        setHasPureoa(lp.includes('pureoa'));
      })
      .catch(() => { /* leave optimistic defaults */ });
    return () => { cancelled = true; };
  }, []);

  const licensed = useMemo(
    () => ENTRIES.filter((e) =>
      e.license === 'qutime' ? hasQutime : e.license === 'pureoa' ? hasPureoa : true,
    ),
    [hasQutime, hasPureoa],
  );

  // Resolve translated strings once so search can match what the user actually
  // reads, and the filter is locale-correct.
  const resolved = useMemo(
    () => licensed.map((e) => ({
      entry: e,
      title: t(e.titleKey),
      desc: e.descKey ? t(e.descKey) : '',
      areaLabel: t(AREA_HEADER[e.area]),
    })),
    [licensed, t],
  );

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return resolved;
    return resolved.filter((r) =>
      `${r.title} ${r.desc} ${r.areaLabel}`.toLowerCase().includes(q),
    );
  }, [resolved, q]);

  const renderCard = (r: { entry: SettingEntry; title: string; desc: string }) => {
    const { entry, title, desc } = r;
    return (
      <Grid item xs={12} sm={6} md={4} key={entry.key}>
        <Paper
          elevation={0}
          onClick={() => navigate(entry.path)}
          sx={{
            p: 2.5,
            borderRadius: 2,
            cursor: 'pointer',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'rgba(0,0,0,0.12)',
            transition: 'border-color 120ms',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: desc ? 1 : 0 }}>
            <Box sx={{ color: 'primary.main', display: 'flex' }}>{entry.icon}</Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', flexGrow: 1 }}>
              {title}
            </Typography>
            {entry.crossModule ? (
              <NorthEastOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
            ) : (
              <ChevronRightOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />
            )}
          </Stack>
          {desc ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', flexGrow: 1 }}>
              {desc}
            </Typography>
          ) : null}
          {entry.crossModule ? (
            <Typography variant="caption" sx={{ mt: desc ? 1 : 0.5, color: 'text.disabled', fontSize: '0.7rem', fontWeight: 600 }}>
              {t('system:settings.opensIn', { area: t(`system:settings.areas.${entry.area}`) })}
            </Typography>
          ) : null}
        </Paper>
      </Grid>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('system:settings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('system:settings.subtitle')}
        </Typography>
      </Box>

      {/* Search — the "find any setting" quick-entry. */}
      <TextField
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        size="small"
        placeholder={t('system:settings.searchPlaceholder')}
        sx={{ width: '100%', maxWidth: 420, mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
      />

      {q ? (
        // Search mode — flat results across all areas.
        matches.length === 0 ? (
          <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              {t('system:settings.noResults', { query })}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2} sx={{ alignSelf: 'stretch' }}>
            {matches.map(renderCard)}
          </Grid>
        )
      ) : (
        // Idle mode — grouped by area.
        AREA_ORDER.map((area) => {
          const items = matches.filter((r) => r.entry.area === area);
          if (items.length === 0) return null;
          return (
            <Box key={area} sx={{ width: '100%', mb: 3 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', display: 'block', mb: 1 }}>
                {t(AREA_HEADER[area])}
              </Typography>
              <Grid container spacing={2} sx={{ alignSelf: 'stretch' }}>
                {items.map(renderCard)}
              </Grid>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default SettingsLanding;
